import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Shuffle, Copy, Eye, X, Play, Pause, Bell, BellOff, Gauge, Timer, Minus, Repeat2, FlipHorizontal2 } from "lucide-react";
import { articlesAPI, communitiesAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import { useReading } from "../context/ReadingContext";
import useAutoFitText from "../hooks/useAutoFitText";
import useStudyTimerExtension from "../hooks/useStudyTimerExtension";
import Spinner from "../components/Spinner";
import {
  copyTextToClipboard,
  formatFlashcardForClipboard,
} from "../utils/clipboard";
import {
  getRememberedTutorialLessonId,
  extractPublicLessonId,
  getPublicLessonPath,
  getTutorialLessonPath,
} from "../constants/tutorialContent";
import "../pages/StudyFlashcardsView.css";

const PublicFlashcards = () => {
  const { lessonId, communityId } = useParams();
  const navigate = useNavigate();
  const isCommunityView = Boolean(communityId);
  const rememberedTutorialLessonId = getRememberedTutorialLessonId();
  const canonicalLessonId = extractPublicLessonId(lessonId);
  const backPath = isCommunityView
    ? `/community/${communityId}/lesson/${lessonId}`
    : canonicalLessonId === rememberedTutorialLessonId
      ? getTutorialLessonPath()
      : getPublicLessonPath(lessonId);
  const [flashcards, setFlashcards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [resetTrigger, setResetTrigger] = useState(false);
  const currentFC = flashcards[currentIndex] || null;
  const { containerRef: qContainerRef, textRef: qTextRef } = useAutoFitText(currentFC?.chapter, { maxFontRem: 1.4, minFontRem: 0.8 });
  const { containerRef: aContainerRef, textRef: aTextRef } = useAutoFitText(currentFC?.body, { maxFontRem: 1.4, minFontRem: 0.8 });
  const [loading, setLoading] = useState(true);
  const [jumpIndex, setJumpIndex] = useState("");
  const [isPaused, setIsPaused] = useState(false);
  const [flashcardMode, setFlashcardMode] = useState("flip");
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showDurationInfo, setShowDurationInfo] = useState(false);
  const [targetHours, setTargetHours] = useState("");
  const [targetMinutes, setTargetMinutes] = useState("");
  const [showTimerMenu, setShowTimerMenu] = useState(false);
  const [timerHours, setTimerHours] = useState("");
  const [timerMinutes, setTimerMinutes] = useState("");
  const [timerEndTime, setTimerEndTime] = useState(null);
  const [timerDisplay, setTimerDisplay] = useState("");
  useStudyTimerExtension(setTimerEndTime, setTimerDisplay);
  const [chimeEnabled, setChimeEnabled] = useState(() => {
    const saved = localStorage.getItem("chimeEnabled");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showPlayIcon, setShowPlayIcon] = useState(false);
  const [showPauseIcon, setShowPauseIcon] = useState(false);
  const [cursorClass, setCursorClass] = useState("cursor-pointer");
  const [progressLineEnabled, setProgressLineEnabled] = useState(() => {
    const saved = localStorage.getItem("progressLineEnabled");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const cursorTimeoutRef = React.useRef(null);
  const startTimeRef = React.useRef(null);
  const pausedTimeRef = React.useRef(null);
  const lastChimeTimeRef = React.useRef(0);
  const hasCalledNextRef = React.useRef(false);
  const showAnswerRef = React.useRef(false);
  const { addToast } = useToasts();
  const { readingModeEnabled: globalReadingMode, toggleReadingMode, speedMultiplier, setSpeedMultiplier } = useReading();
  const isFlipMode = flashcardMode === "flip";

  useEffect(() => {
    showAnswerRef.current = showAnswer;
  }, [showAnswer]);

  useEffect(() => {
    const loadFlashcards = async () => {
      try {
        setLoading(true);
        const response = isCommunityView
          ? await communitiesAPI.getLessonItems(communityId, lessonId, "flashcard")
          : await articlesAPI.getPublicFlashcards(lessonId);
        const rawFlashcards = isCommunityView ? response.data.items : response.data;
        setFlashcards(rawFlashcards);
        setLoading(false);
      } catch {
        addToast("Failed to load flashcards", "error-warning");
        setLoading(false);
      }
    };

    if (lessonId) {
      loadFlashcards();
    }
  }, [addToast, communityId, isCommunityView, lessonId]);

  const handlePrevious = () => {
    setCurrentIndex((previousIndex) => {
      const newIndex =
        previousIndex > 0 ? previousIndex - 1 : flashcards.length - 1;

      if (newIndex === previousIndex) {
        setResetTrigger((r) => !r);
      }

      return newIndex;
    });
    setIsPaused(false);
  };

  const handleNext = (playChime = true) => {
    if (flashcards.length === 0) return;

    if (chimeEnabled && playChime) {
      try {
        const currentTime = Date.now();
        const timeSinceLastChime = currentTime - lastChimeTimeRef.current;

        if (timeSinceLastChime >= 1000) {
          lastChimeTimeRef.current = currentTime;
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const now = audioContext.currentTime;
          [523.25].forEach((freq) => {
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            oscillator.frequency.value = freq;
            oscillator.type = "sine";
            gainNode.gain.setValueAtTime(0, now);
            gainNode.gain.linearRampToValueAtTime(0.08, now + 0.02);
            gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.8);
            oscillator.start(now);
            oscillator.stop(now + 0.8);
          });
        }
      } catch (error) {
        console.warn("Flashcard chime failed, continuing navigation.", error);
      }
    }

    setCurrentIndex((previousIndex) => {
      const newIndex =
        previousIndex < flashcards.length - 1 ? previousIndex + 1 : 0;

      if (newIndex === previousIndex) {
        setResetTrigger((r) => !r);
      }

      return newIndex;
    });
    setIsPaused(false);
  };

  const calculateTotalDuration = () => {
    const remaining = flashcards.slice(currentIndex);
    let totalSeconds = 0;
    remaining.forEach((fc) => {
      const words = (fc.chapter + " " + fc.body).split(" ").length;
      totalSeconds += Math.max(5, Math.ceil((words / 200) * 60 * speedMultiplier));
    });
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    return h > 0 ? `${h}h${m}` : m > 0 ? `${m}m` : `${s}s`;
  };

  const togglePause = () => {
    setIsPaused((previousIsPaused) => {
      const newPauseState = !previousIsPaused;

      if (newPauseState) {
        setShowPauseIcon(true);
        setTimeout(() => setShowPauseIcon(false), 1000);
      } else {
        setShowPlayIcon(true);
        setTimeout(() => setShowPlayIcon(false), 1000);
      }

      return newPauseState;
    });
  };

  const handleShuffle = () => {
    const randomIndex = Math.floor(Math.random() * flashcards.length);
    setCurrentIndex(randomIndex);
    addToast(`Random flashcard #${randomIndex + 1}`, "success-info");
  };

  const handleFlipModeToggle = () => {
    setFlashcardMode((previousMode) => {
      const nextMode = previousMode === "timed" ? "flip" : "timed";
      setShowAnswer(false);
      setIsPaused(false);
      setShowSpeedMenu(false);
      setShowDurationInfo(false);
      setShowTimerMenu(false);
      addToast(
        nextMode === "flip" ? "Flip mode ON" : "Timed mode ON",
        "success-info",
      );
      return nextMode;
    });
  };

  const handleFlipAction = () => {
    if (!isFlipMode) {
      return;
    }

    if (!showAnswerRef.current) {
      setShowAnswer(true);
      return;
    }

    handleNext();
  };

  const handleCopy = async () => {
    const currentFC = flashcards[currentIndex];
    const text = formatFlashcardForClipboard(currentFC);

    try {
      await copyTextToClipboard(text);
      addToast("Flashcard copied", "success-info");
    } catch {
      addToast("Failed to copy", "error-warning");
    }
  };

  const calculateSpeedForTargetTime = () => {
    const targetSeconds = (parseInt(targetHours) || 0) * 3600 + (parseInt(targetMinutes) || 0) * 60;
    if (targetSeconds === 0) return;
    const calculatedSpeed = Math.max(1, Math.min(50, Math.round(targetSeconds / 60)));
    setSpeedMultiplier(calculatedSpeed);
    setShowSpeedMenu(false);
    addToast(`Speed adjusted to ${calculatedSpeed}x`, "success-info");
    setTargetHours("");
    setTargetMinutes("");
  };

  const setTimer = () => {
    const hours = parseInt(timerHours) || 0;
    const minutes = parseInt(timerMinutes) || 0;
    if (hours === 0 && minutes === 0) return;
    const setAt = Date.now();
    const durationMinutes = hours * 60 + minutes;
    const endTime = setAt + hours * 3600000 + minutes * 60000;
    setTimerEndTime(endTime);
    localStorage.setItem("studyTimer", endTime.toString());
    localStorage.setItem(
      "studyTimerInfo",
      JSON.stringify({ setAt, durationMinutes })
    );
    setShowTimerMenu(false);
    setTimerHours("");
    setTimerMinutes("");
    let timeStr = hours > 0 && minutes > 0 ? `${hours}h ${minutes}m` : hours > 0 ? `${hours}h` : `${minutes}m`;
    addToast(`Timer set for ${timeStr}`, "success-info");
  };

  const clearTimer = () => {
    setTimerEndTime(null);
    localStorage.removeItem("studyTimer");
    localStorage.removeItem("studyTimerInfo");
    setTimerDisplay("");
    addToast("Timer cleared", "success-info");
  };

  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        if (globalReadingMode) {
          toggleReadingMode();
          if (document.fullscreenElement) {
            document.exitFullscreen();
          }
          addToast("Reading mode OFF", "success-info");
        } else {
          navigate(backPath);
        }
      }
    };
    window.addEventListener("keydown", handleEscKey, true);
    return () => window.removeEventListener("keydown", handleEscKey, true);
  }, [backPath, globalReadingMode, toggleReadingMode, addToast, navigate]);

  useEffect(() => {
    const savedTimer = localStorage.getItem("studyTimer");
    if (savedTimer) {
      const parsedEndTime = parseInt(savedTimer, 10);
      if (Number.isFinite(parsedEndTime) && parsedEndTime > Date.now()) {
        setTimerEndTime(parsedEndTime);
      } else {
        localStorage.removeItem("studyTimer");
        localStorage.removeItem("studyTimerInfo");
      }
    }
  }, []);

  useEffect(() => {
    if (flashcards.length === 0) return;
    const currentFC = flashcards[currentIndex];
    const wordsCount = (currentFC.chapter + " " + currentFC.body).split(" ").length;
    setShowAnswer(false);
    setIsPaused(false);

    if (isFlipMode) {
      setTimeLeft(0);
      setTotalTime(0);
      startTimeRef.current = null;
      pausedTimeRef.current = null;
      hasCalledNextRef.current = false;
      return;
    }

    const readingTime = Math.max(5, Math.ceil((wordsCount / 200) * 60 * speedMultiplier));
    setTimeLeft(readingTime);
    setTotalTime(readingTime);
    startTimeRef.current = performance.now();
    pausedTimeRef.current = null;
    hasCalledNextRef.current = false;
  }, [currentIndex, flashcards, speedMultiplier, resetTrigger, isFlipMode]);

  useEffect(() => {
    if (!totalTime || isFlipMode) return;
    const updateTimer = () => {
      if (isPaused) {
        if (!pausedTimeRef.current) {
          pausedTimeRef.current = performance.now();
        }
        return;
      }
      if (pausedTimeRef.current) {
        const pausedDuration = performance.now() - pausedTimeRef.current;
        startTimeRef.current += pausedDuration;
        pausedTimeRef.current = null;
      }
      const elapsed = (performance.now() - startTimeRef.current) / 1000;
      const newTimeLeft = Math.max(0, totalTime - elapsed);
      setTimeLeft(newTimeLeft);
      if (elapsed >= totalTime / 2 && !showAnswer) {
        setShowAnswer(true);
      }
      if (elapsed >= totalTime && !hasCalledNextRef.current) {
        hasCalledNextRef.current = true;
        handleNext();
      }
    };
    const timer = setInterval(updateTimer, 100);
    return () => clearInterval(timer);
  }, [totalTime, isPaused, showAnswer, isFlipMode]);

  useEffect(() => {
    if (!timerEndTime) {
      setTimerDisplay("");
      return;
    }
    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = timerEndTime - now;
      if (remaining <= 0) {
        setTimerDisplay("0s");
        return;
      }
      const hours = Math.floor(remaining / 3600000);
      const minutes = Math.floor((remaining % 3600000) / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      if (hours > 0) {
        setTimerDisplay(`${hours}h${minutes}`);
      } else if (minutes > 0) {
        setTimerDisplay(`${minutes}m${seconds}`);
      } else {
        setTimerDisplay(`${seconds}s`);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [timerEndTime]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      const isSpaceKey =
        e.key === " " || e.key === "Spacebar" || e.code === "Space";
      const activeElement = document.activeElement;
      if (
        activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "TEXTAREA"
      ) {
        return;
      }

      if (e.key === "ArrowLeft" || (isFlipMode && e.key === "ArrowUp")) {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        handleNext(false);
      } else if (isFlipMode && e.key === "ArrowDown") {
        e.preventDefault();
        handleFlipAction();
      } else if (isSpaceKey && !isFlipMode) {
        e.preventDefault();
        e.stopPropagation();
        if (activeElement instanceof HTMLElement && activeElement !== document.body) {
          activeElement.blur();
        }
        togglePause();
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [flashcards.length, isFlipMode, showAnswer, chimeEnabled]);

  useEffect(() => {
    const handleMouseMove = () => {
      document.body.classList.remove("hide-cursor");
      if (cursorTimeoutRef.current) {
        clearTimeout(cursorTimeoutRef.current);
      }
      cursorTimeoutRef.current = setTimeout(() => {
        document.body.classList.add("hide-cursor");
      }, 10000);
    };
    window.addEventListener("mousemove", handleMouseMove);
    handleMouseMove();
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.body.classList.remove("hide-cursor");
      if (cursorTimeoutRef.current) {
        clearTimeout(cursorTimeoutRef.current);
      }
    };
  }, []);

  const handleCardClick = (e) => {
    if (e.target.closest(".study-fc-buttons-row") || e.target.closest(".study-fc-progress-bar") || e.target.closest(".study-fc-nav-btn")) {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    const clickPosition = x / width;
    if (clickPosition < 0.15) {
      handlePrevious();
    } else if (clickPosition > 0.85) {
      handleNext(false);
    } else {
      if (isFlipMode) {
        handleFlipAction();
      } else {
        togglePause();
      }
    }
  };

  const handleMouseMove = (e) => {
    if (e.target.closest(".study-fc-buttons-row") || e.target.closest(".study-fc-progress-bar")) {
      setCursorClass("cursor-default");
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const width = rect.width;
    const height = rect.height;
    const position = x / width;
    const verticalPosition = y / height;
    if (verticalPosition > 0.85) {
      setCursorClass("cursor-default");
      return;
    }
    if (position < 0.15) {
      setCursorClass("cursor-left");
    } else if (position > 0.85) {
      setCursorClass("cursor-right");
    } else {
      setCursorClass("cursor-pointer");
    }
  };

  if (loading) return <Spinner visible={true} />;

  if (flashcards.length === 0) {
    return (
      <div className="public-fc-empty">
        <h2>No flashcards available</h2>
        <button
          className="public-fc-empty-button"
          onClick={() => navigate(backPath)}
        >
          {isCommunityView ? "Back to Community Lesson" : "Back to Home"}
        </button>
      </div>
    );
  }

  const progressValue =
    totalTime > 0 ? ((totalTime - timeLeft) / totalTime) * 100 : 0;

  return (
    <div className="public-fc-wrapper">
      <button
        className="public-fc-home-exit-button"
        onClick={() => navigate(backPath)}
        title={isCommunityView ? "Back to Community Lesson" : "Back to Home"}
      >
        <X size={20} />
      </button>

      <div className={`study-fc-container ${cursorClass}`} onClick={handleCardClick} onMouseMove={handleMouseMove}>
        <button className={`study-fc-nav-btn study-fc-nav-prev ${globalReadingMode ? "study-fc-controls-hidden" : ""}`} onClick={handlePrevious}>‹‹</button>
        <button className={`study-fc-nav-btn study-fc-nav-next ${globalReadingMode ? "study-fc-controls-hidden" : ""}`} onClick={() => handleNext(false)}>››</button>
        <div className="study-fc-card">
          <div className="study-fc-card-split">
            <div className="study-fc-card-left" ref={qContainerRef}>
              <p ref={qTextRef} key={`q-${currentIndex}`}>{currentFC.chapter}</p>
            </div>
            <div className={`study-fc-card-right ${showAnswer ? 'reveal' : 'hidden'}`} ref={aContainerRef}>
              <p ref={aTextRef} key={`a-${currentIndex}-${showAnswer}`}>{currentFC.body}</p>
            </div>
          </div>
          {showPauseIcon && (
            <div className="study-fc-pause-overlay" key="pause">
              <Pause size={50} strokeWidth={2.5} />
            </div>
          )}
          {showPlayIcon && (
            <div className="study-fc-pause-overlay" key="play">
              <Play size={50} strokeWidth={2.5} />
            </div>
          )}
          <div className={`study-fc-progress-bar ${(progressLineEnabled && !isFlipMode) ? "" : "is-hidden"}`}>
            <progress
              className="study-fc-progress-meter"
              value={progressValue}
              max="100"
              aria-label="Flashcard progress"
            />
          </div>
        </div>

        <div className={`study-fc-buttons-row ${globalReadingMode ? "reading-mode-fade" : ""}`}>
          <div className="study-fc-button-group">
            <button className="study-fc-control-btn" onClick={() => { toggleReadingMode(); if (!globalReadingMode) { document.documentElement.requestFullscreen(); addToast("Use keyboard to navigate", "success-info"); } else if (document.fullscreenElement) { document.exitFullscreen(); } addToast(`Reading mode ${globalReadingMode ? "OFF" : "ON"}`, "success-info"); }} title="Reading mode"><Eye size={18} /></button>
            <button className="study-fc-control-btn" onClick={() => { const newValue = !chimeEnabled; setChimeEnabled(newValue); localStorage.setItem("chimeEnabled", JSON.stringify(newValue)); addToast(`Chime ${chimeEnabled ? "OFF" : "ON"}`, "success-info"); }} title={chimeEnabled ? "Chime ON" : "Chime OFF"}>{chimeEnabled ? <Bell size={18} /> : <BellOff size={18} />}</button>
            {isFlipMode ? (
              <button className="study-fc-control-btn" onClick={handleFlipAction} title={showAnswer ? "Next flashcard" : "Flip"}>
                <FlipHorizontal2 size={18} />
              </button>
            ) : (
              <button className="study-fc-control-btn" onClick={() => { const newValue = !progressLineEnabled; setProgressLineEnabled(newValue); localStorage.setItem("progressLineEnabled", JSON.stringify(newValue)); addToast(`Progress line ${progressLineEnabled ? "OFF" : "ON"}`, "success-info"); }} title={progressLineEnabled ? "Progress line ON" : "Progress line OFF"}><Minus size={18} className={`study-fc-minus-icon ${progressLineEnabled ? "" : "is-dim"}`} /></button>
            )}
            <button className={`study-fc-control-btn ${isFlipMode ? "is-active" : ""}`} onClick={handleFlipModeToggle} title={isFlipMode ? "Switch to timed mode" : "Switch to flip mode"}><Repeat2 size={18} /></button>
          </div>
          {!isFlipMode && (
            <div className="study-fc-menu-anchor">
              <button className="study-fc-control-btn" onClick={() => setShowSpeedMenu(!showSpeedMenu)} title="Speed"><Gauge size={14} /><span className="study-fc-speed-label">{speedMultiplier === 5 ? 3 : speedMultiplier === 15 ? 10 : speedMultiplier}x</span></button>
              {showSpeedMenu && (
                <div className="study-fc-popup-menu">
                  {[1, 3, 10].map((displaySpeed) => { const actualSpeed = displaySpeed === 3 ? 5 : displaySpeed === 10 ? 15 : displaySpeed; return (<button key={displaySpeed} onClick={() => { setSpeedMultiplier(actualSpeed); setShowSpeedMenu(false); addToast(`Speed: ${displaySpeed}x`, "success-info"); }} className={`study-fc-popup-option ${speedMultiplier === actualSpeed ? "is-active" : ""}`}>{displaySpeed}x</button>); })}
                  <div className="study-fc-popup-inputs"><input type="number" placeholder="h" value={targetHours} onChange={(e) => setTargetHours(e.target.value)} onKeyPress={(e) => e.key === "Enter" && calculateSpeedForTargetTime()} className="study-fc-popup-input" /><span className="study-fc-popup-separator">:</span><input type="number" placeholder="m" value={targetMinutes} onChange={(e) => setTargetMinutes(e.target.value)} onKeyPress={(e) => e.key === "Enter" && calculateSpeedForTargetTime()} className="study-fc-popup-input" /></div>
                  <button onClick={calculateSpeedForTargetTime} className="study-fc-popup-set">Set</button>
                </div>
              )}
            </div>
          )}
          {!isFlipMode && (
            <button className="study-fc-control-btn study-fc-duration-btn" onClick={() => { setShowDurationInfo(true); setTimeout(() => setShowDurationInfo(false), 5000); }} title="Total duration"><span className="study-fc-duration-label">{calculateTotalDuration()}</span>{showDurationInfo && (<div className="study-fc-duration-tooltip">{flashcards.length - currentIndex} flashcards left at {speedMultiplier === 5 ? 3 : speedMultiplier === 15 ? 10 : speedMultiplier}x</div>)}</button>
          )}
          <input type="number" min="1" max={flashcards.length} value={jumpIndex} onChange={(e) => setJumpIndex(e.target.value)} onKeyPress={(e) => { if (e.key === "Enter") { const index = parseInt(jumpIndex) || 1; if (index >= 1 && index <= flashcards.length) { setCurrentIndex(index - 1); setIsPaused(false); addToast(`${index} / ${flashcards.length}`, "success-info"); e.target.blur(); } } }} onFocus={(e) => e.target.select()} onBlur={() => setJumpIndex("")} placeholder={currentIndex + 1} className="study-fc-control-input" title="Jump to flashcard" />
          {!isFlipMode && (
            <button
              className="study-fc-control-btn"
              onClick={togglePause}
              onKeyUp={(e) => {
                if (
                  e.key === " " ||
                  e.key === "Spacebar" ||
                  e.code === "Space"
                ) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              title={isPaused ? "Resume" : "Pause"}
            >
              {isPaused ? <Play size={18} /> : <Pause size={18} />}
            </button>
          )}
          <div className="study-fc-total-display" title="Total flashcards">{flashcards.length}</div>
          <button className="study-fc-control-btn" onClick={handleShuffle} title="Random flashcard"><Shuffle size={18} /></button>
          <div className="study-fc-button-group">
            <div className="study-fc-menu-anchor">
              <button className="study-fc-control-btn study-fc-timer-btn" onClick={() => timerEndTime ? clearTimer() : setShowTimerMenu(!showTimerMenu)} title={timerEndTime ? "Clear timer" : "Set timer"}>{timerEndTime ? (<><Timer size={14} /><span className="study-fc-timer-label">{timerDisplay}</span></>) : (<Timer size={18} />)}</button>
              {showTimerMenu && (<div className="study-fc-popup-menu"><div className="study-fc-popup-inputs"><input type="number" placeholder="h" value={timerHours} onChange={(e) => setTimerHours(e.target.value)} onKeyPress={(e) => e.key === "Enter" && setTimer()} className="study-fc-popup-input" /><span className="study-fc-popup-separator">:</span><input type="number" placeholder="m" value={timerMinutes} onChange={(e) => setTimerMinutes(e.target.value)} onKeyPress={(e) => e.key === "Enter" && setTimer()} className="study-fc-popup-input" /></div><button onClick={setTimer} className="study-fc-popup-set">Set</button></div>)}
            </div>
          </div>
          <button className="study-fc-control-btn" onClick={handleCopy} title="Copy flashcard"><Copy size={18} /></button>
        </div>

        {globalReadingMode && (
          <button
            className="reading-mode-toggle-persistent"
            onClick={() => {
              toggleReadingMode();
              if (document.fullscreenElement) {
                document.exitFullscreen();
              }
              addToast("Reading mode OFF", "success-info");
            }}
            title="Exit reading mode"
          >
            <X size={20} />
          </button>
        )}
      </div>
    </div>
  );
};

export default PublicFlashcards;
